import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, FileText, ThumbsDown, ThumbsUp } from "lucide-react";
import { listGenieFeedback, updateGenieFeedback } from "@hp/api-client";
import type { GenieFeedback, GenieFeedbackStatus } from "@hp/api-client";
import { useAuth } from "../auth/AuthContext";
import { PageHeader } from "../components/layout/PageHeader";
import { ErrorState, LoadingState } from "../components/Feedback";

type Filter = GenieFeedbackStatus | "all";

const FILTERS: { key: Filter; label: string }[] = [
  { key: "open", label: "Open" },
  { key: "resolved", label: "Resolved" },
  { key: "all", label: "All" },
];

function when(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function RatingMark({ rating }: { rating: GenieFeedback["rating"] }) {
  if (rating === "up") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-800">
        <ThumbsUp className="h-3 w-3" aria-hidden /> Useful
      </span>
    );
  }
  if (rating === "down") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 text-xs font-medium text-rose-800">
        <ThumbsDown className="h-3 w-3" aria-hidden /> Not useful
      </span>
    );
  }
  return <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs font-medium text-zinc-600">Comment</span>;
}

/** One piece of feedback: who and when, the rating and comment, the question and answer behind it, and resolving it. */
function FeedbackItem({ item }: { item: GenieFeedback }) {
  const queryClient = useQueryClient();
  const [showAnswer, setShowAnswer] = React.useState(false);
  const [note, setNote] = React.useState(item.resolution_note);
  const update = useMutation({
    mutationFn: (status: GenieFeedbackStatus) => updateGenieFeedback(item.id, status, status === "resolved" ? note : ""),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["genie-feedback"] }),
  });
  const resolved = item.status === "resolved";
  return (
    <li className="rounded-lg border border-zinc-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <RatingMark rating={item.rating} />
        <span className={["rounded px-1.5 py-0.5 text-xs font-medium", resolved ? "bg-sky-50 text-sky-800" : "bg-amber-50 text-amber-800"].join(" ")}>
          {resolved ? "Resolved" : "Open"}
        </span>
        <span className="text-xs text-zinc-500">
          {item.user_name || item.user_email} · {when(item.created_at)}
        </span>
      </div>
      {item.comment ? <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-900">{item.comment}</p> : null}
      {item.question ? (
        <p className="mt-2 text-xs text-zinc-600">
          <span className="font-semibold text-zinc-700">Question: </span>
          <span className="line-clamp-3">{item.question}</span>
        </p>
      ) : null}
      {item.answer ? (
        <div className="mt-1.5">
          <button
            type="button"
            onClick={() => setShowAnswer((v) => !v)}
            aria-expanded={showAnswer}
            className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-800"
          >
            {showAnswer ? <ChevronDown className="h-3.5 w-3.5" aria-hidden /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden />}
            The answer it was given{item.sources.length ? ` and ${item.sources.length} source${item.sources.length === 1 ? "" : "s"}` : ""}
          </button>
          {showAnswer ? (
            <div className="mt-1.5 space-y-2 rounded-md bg-zinc-50 px-3 py-2">
              <p className="whitespace-pre-wrap text-xs leading-relaxed text-zinc-700">{item.answer}</p>
              {item.sources.length ? (
                <ul className="space-y-0.5">
                  {item.sources.map((s, i) => (
                    <li key={i} className="flex items-start gap-1 text-[0.6875rem] text-zinc-600">
                      <FileText className="mt-px h-3 w-3 flex-none" aria-hidden />
                      <span>
                        {s.label}
                        <span className="text-zinc-400">
                          {" "}
                          · {s.file_name}
                          {s.page ? `, page ${s.page}` : ""}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="mt-3 border-t border-zinc-100 pt-3">
        {resolved ? (
          <div className="flex flex-wrap items-start justify-between gap-2">
            <p className="text-xs text-zinc-600">
              Resolved by {item.resolved_by || "an admin"}
              {item.resolved_at ? ` on ${when(item.resolved_at)}` : ""}
              {item.resolution_note ? <span className="mt-0.5 block whitespace-pre-wrap text-zinc-800">{item.resolution_note}</span> : null}
            </p>
            <button
              type="button"
              onClick={() => update.mutate("open")}
              disabled={update.isPending}
              className="rounded-md border border-zinc-200 px-2.5 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
            >
              Reopen
            </button>
          </div>
        ) : (
          <form
            className="flex flex-col gap-2 sm:flex-row sm:items-start"
            onSubmit={(e) => {
              e.preventDefault();
              update.mutate("resolved");
            }}
          >
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={1}
              maxLength={5000}
              placeholder="What was done (optional)"
              aria-label="What was done about this feedback"
              className="min-h-9 flex-1 resize-y rounded-md border border-zinc-300 px-2.5 py-1.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-500 focus:outline-none"
            />
            <button
              type="submit"
              disabled={update.isPending}
              className="rounded-md bg-zinc-900 px-3 py-2 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
            >
              {update.isPending ? "Saving..." : "Mark resolved"}
            </button>
          </form>
        )}
        {update.isError ? <p className="mt-1 text-xs text-rose-700">Could not save. Try again.</p> : null}
      </div>
    </li>
  );
}

/** Genie feedback (admins): what people said about HP Genie's answers, open first, each one resolved with a note. */
export function GenieFeedbackPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [filter, setFilter] = React.useState<Filter>("open");
  const query = useQuery({ queryKey: ["genie-feedback", filter], queryFn: () => listGenieFeedback(filter), enabled: isAdmin });

  if (!isAdmin) {
    return (
      <div className="page-stack">
        <PageHeader title="Genie feedback" description="Only an admin can read the feedback on HP Genie's answers." />
      </div>
    );
  }
  return (
    <div className="page-stack">
      <PageHeader title="Genie feedback" description="What people said about HP Genie's answers. Read each one, fix what it points to, and mark it resolved with what was done." />
      <div role="tablist" aria-label="Which feedback" className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            role="tab"
            aria-selected={filter === f.key}
            onClick={() => setFilter(f.key)}
            className={["rounded-md px-3 py-1.5 text-sm font-medium", filter === f.key ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"].join(" ")}
          >
            {f.label}
            {filter === f.key && query.data ? <span className="ml-1.5 tabular-nums text-zinc-300">{query.data.length}</span> : null}
          </button>
        ))}
      </div>
      {query.isPending ? <LoadingState label="Loading feedback..." /> : null}
      {query.isError ? <ErrorState error={query.error} label="Could not load the feedback" /> : null}
      {query.data ? (
        query.data.length === 0 ? (
          <p className="rounded-lg border border-dashed border-zinc-200 bg-white px-4 py-8 text-center text-sm text-zinc-500">
            {filter === "open" ? "No open feedback." : filter === "resolved" ? "Nothing resolved yet." : "No feedback yet."}
          </p>
        ) : (
          <ul className="grid gap-3 2xl:grid-cols-2">
            {query.data.map((item) => (
              <FeedbackItem key={item.id} item={item} />
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}
